import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NameTagComponent } from '@components/account/profile/name-tag/name-tag.component';
import { ProfilePictureComponent } from '@components/account/profile/profile-picture/profile-picture.component';
import { ProsemirrorModule } from '@components/misc/prosemirror/prosemirror.module';
import { SharedModule } from '@components/shared/shared.module';
import { Participant } from '@models/Sessions/Participant';
import { Session } from '@models/Sessions/Session';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthenticationService } from '@services/auth/authentication.service';
import { BusService } from '@services/bus.service';
import { ConfigService } from '@services/config.service';
import { EventsService } from '@services/events.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CheckboxModule } from 'primeng/checkbox';
import { DataViewModule } from 'primeng/dataview';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';

import { AddParticipantModalComponent } from './add-participant-modal/add-participant-modal.component';

@Component({
  selector: 'app-session-settings',
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    ProsemirrorModule,
    FormsModule,
    InputTextModule,
    SelectModule,
    TextareaModule,
    CheckboxModule,
    DataViewModule,
    ProfilePictureComponent,
    NameTagComponent,
  ],
  templateUrl: './session-settings.component.html',
  styleUrls: ['./session-settings.component.scss'],
})
export class SessionSettingsComponent implements OnInit, OnDestroy {
  @Input() session: Session;

  settingsChanged = false;
  settingsSaved = false;
  activeUpload = false;

  descValid = true;

  constructor(
    public appConfig: ConfigService,
    public auth: AuthenticationService,
    private bus: BusService,
    private events: EventsService,
    private modalService: NgbModal,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
  ) {}

  ngOnInit() {
    if (!this.session.config.page_size) {
      this.session.config.page_size = 16;
    }

    if (this.session.config.suggest_tags !== false) {
      this.session.config.suggest_tags = true;
    }

    if (!this.session.config.people) {
      this.session.config.people = { active: true };
    }

    if (!this.session.config.pdf_exports) {
      this.session.config.pdf_exports = { active: true };
    }

    if (!this.session.config.visibility) {
      this.session.config.visibility = 7;
    }

    if (!this.session.config.layout) {
      this.session.config.layout = 'cards';
    }
    this.subscribe();
  }

  ngOnDestroy() {
    this.unsubscribe();
  }

  updateRawDesc(desc: string) {
    this.session.config.description = desc;
    this.settingsChanged = true;
  }

  saveConfig() {
    this.bus.publish(
      this.events.requested.action.session.updateConfig,
      this.session,
    );
    this.settingsChanged = false;
    this.settingsSaved = true;
  }

  uploadStart() {
    this.activeUpload = true;
  }

  uploadComplete(key) {
    this.activeUpload = false;
    this.session.config.thumbnail_id = key;
    this.settingsChanged = true;
  }

  logoUploadComplete(key) {
    this.activeUpload = false;
    this.session.config.organizer.logo_id = key;
    this.settingsChanged = true;
  }

  get selectableRoles() {
    return this.appConfig.rolesSelectOptions.filter(role => role.value !== 0);
  }

  get visibilityRoles() {
    return this.appConfig.rolesSelectOptions.concat({
      name: 'Everyone',
      value: 7,
    });
  }

  addParticipant() {
    const mdref = this.modalService.open(AddParticipantModalComponent);
    mdref.componentInstance.session = this.session;
    mdref.result.then(res => {
      const team =
        this.session.config.teams.find(t => t.name === 'unassigned') ||
        this.session.config.teams[0];
      if (res) {
        this.bus.publish(
          this.events.requested.action.session.participants.add,
          {
            email: res.email,
            user: res,
            role: 2,
            team: team._id,
            votes_for: [team._id],
          },
          this.session,
        );
      }
    });
  }

  deleteParticipant(participant: Participant) {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete <strong>${participant.user.name}</strong> from <strong>${this.session.config.title}</strong>?`,
      header: 'Delete Participant',
      rejectButtonProps: {
        label: 'No',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Yes',
        severity: 'danger',
      },
      accept: () => {
        this.bus.publish(
          this.events.requested.action.session.participants.delete,
          participant,
          this.session,
        );
      },
    });
  }

  get isAdmin() {
    return this.auth.isAdmin;
  }

  private handleParticipantDeleteSuccess() {
    this.messageService.add({
      severity: 'success',
      summary: 'Participant Deleted',
      detail: 'The participant has been deleted.',
    });
  }

  private subscribe() {
    this.bus.subscribe(
      this.events.received.action.session.participants.delete.success,
      this.handleParticipantDeleteSuccess,
      this,
    );
  }

  private unsubscribe() {
    this.bus.unsubscribe(
      this.events.received.action.session.participants.delete.success,
      this.handleParticipantDeleteSuccess,
    );
  }
}
